import { ReactNode } from 'react';
import { Check } from 'lucide-react';
import { clsx } from 'clsx';

/**
 * Progress Bar Component
 */
export interface ProgressBarProps {
  progress: number;
  label?: string;
  showPercentage?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'success' | 'warning' | 'error';
  className?: string;
}

export const ProgressBar = ({
  progress,
  label,
  showPercentage = true,
  size = 'md',
  variant = 'primary',
  className = '',
}: ProgressBarProps) => {
  const sizeClasses = {
    sm: 'h-1',
    md: 'h-2',
    lg: 'h-3',
  };

  const variantClasses = {
    primary: 'bg-primary-600',
    success: 'bg-green-600',
    warning: 'bg-amber-600',
    error: 'bg-red-600',
  };

  const normalizedProgress = Math.min(100, Math.max(0, progress));

  return (
    <div className={`w-full ${className}`}>
      {(label || showPercentage) && (
        <div className="flex justify-between items-center mb-2">
          {label && <span className="text-sm font-medium text-gray-700">{label}</span>}
          {showPercentage && (
            <span className="text-sm font-semibold text-gray-900">
              {Math.round(normalizedProgress)}%
            </span>
          )}
        </div>
      )}
      <div className={`w-full bg-gray-200 rounded-full overflow-hidden ${sizeClasses[size]}`}>
        <div
          className={`${sizeClasses[size]} ${variantClasses[variant]} rounded-full transition-all duration-500 ease-out`}
          style={{ width: `${normalizedProgress}%` }}
          role="progressbar"
          aria-valuenow={normalizedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
};

/**
 * Step Progress Component
 */
export interface Step {
  id: string;
  label: string;
  description?: string;
}

export interface StepProgressProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
  variant?: 'horizontal' | 'vertical';
}

export const StepProgress = ({
  steps,
  currentStep,
  onStepClick,
  variant = 'horizontal',
}: StepProgressProps) => {
  const isStepComplete = (index: number) => index < currentStep;
  const isStepCurrent = (index: number) => index === currentStep;

  if (variant === 'vertical') {
    return (
      <div className="space-y-4">
        {steps.map((step, index) => {
          const complete = isStepComplete(index);
          const current = isStepCurrent(index);

          return (
            <div key={step.id} className="flex gap-4">
              {/* Step indicator */}
              <div className="flex flex-col items-center">
                <button
                  onClick={() => onStepClick?.(index)}
                  disabled={!onStepClick}
                  className={clsx(
                    'w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-200',
                    complete && 'bg-primary-600 text-white',
                    current && 'bg-primary-600 text-white ring-4 ring-primary-100',
                    !complete && !current && 'bg-gray-200 text-gray-600',
                    onStepClick && 'cursor-pointer hover:scale-110'
                  )}
                  aria-current={current ? 'step' : undefined}
                >
                  {complete ? <Check className="w-5 h-5" /> : index + 1}
                </button>
                {index < steps.length - 1 && (
                  <div className={clsx(
                    'w-0.5 h-12 mt-2',
                    complete ? 'bg-primary-600' : 'bg-gray-200'
                  )} />
                )}
              </div>

              {/* Step content */}
              <div className="flex-1 pb-8">
                <h4 className={clsx(
                  'font-semibold mb-1',
                  current ? 'text-primary-600' : 'text-gray-900'
                )}>
                  {step.label}
                </h4>
                {step.description && (
                  <p className="text-sm text-gray-600">{step.description}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Horizontal variant
  return (
    <div className="w-full">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const complete = isStepComplete(index);
          const current = isStepCurrent(index);

          return (
            <div key={step.id} className="flex items-center flex-1">
              {/* Step indicator */}
              <div className="flex flex-col items-center">
                <button
                  onClick={() => onStepClick?.(index)}
                  disabled={!onStepClick}
                  className={clsx(
                    'w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-200 mb-2',
                    complete && 'bg-primary-600 text-white',
                    current && 'bg-primary-600 text-white ring-4 ring-primary-100',
                    !complete && !current && 'bg-gray-200 text-gray-600',
                    onStepClick && 'cursor-pointer hover:scale-110'
                  )}
                  aria-current={current ? 'step' : undefined}
                >
                  {complete ? <Check className="w-5 h-5" /> : index + 1}
                </button>
                <span className={clsx(
                  'text-xs font-medium text-center',
                  current ? 'text-primary-600' : 'text-gray-600'
                )}>
                  {step.label}
                </span>
              </div>

              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className={clsx(
                  'flex-1 h-0.5 mx-2 transition-colors duration-300',
                  complete ? 'bg-primary-600' : 'bg-gray-200'
                )} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Circular Progress Component
 */
export interface CircularProgressProps {
  progress: number;
  size?: number;
  strokeWidth?: number;
  label?: ReactNode;
  variant?: 'primary' | 'success' | 'warning' | 'error';
}

export const CircularProgress = ({
  progress,
  size = 120,
  strokeWidth = 8,
  label,
  variant = 'primary',
}: CircularProgressProps) => {
  const normalizedProgress = Math.min(100, Math.max(0, progress));
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (normalizedProgress / 100) * circumference;

  const variantColors = {
    primary: '#2563eb',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
  };

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={variantColors[variant]}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {label || (
          <span className="text-2xl font-bold text-gray-900">
            {Math.round(normalizedProgress)}%
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Loading Progress Bar (Indeterminate)
 */
export const LoadingProgressBar = ({ 
  variant = 'primary',
  className = '' 
}: { 
  variant?: 'primary' | 'success' | 'warning' | 'error';
  className?: string;
}) => {
  const variantClasses = {
    primary: 'bg-primary-600',
    success: 'bg-green-600',
    warning: 'bg-amber-600',
    error: 'bg-red-600',
  };

  return (
    <div className={`w-full h-1 bg-gray-200 rounded-full overflow-hidden ${className}`}>
      <div 
        className={`h-full ${variantClasses[variant]} rounded-full animate-progress`}
        style={{
          animation: 'progress 1.5s ease-in-out infinite',
        }}
      />
      <style>{`
        @keyframes progress {
          0% {
            transform: translateX(-100%);
            width: 30%;
          }
          50% {
            width: 70%;
          }
          100% {
            transform: translateX(400%);
            width: 30%;
          }
        }
      `}</style>
    </div>
  );
};
